/**
 * Shared handling for mandatory fields.
 *
 * A form marks each required input with `data-required-field="<key>"`, then
 * passes the same keys to `reportFirstMissingField` when the user tries to
 * save. The first missing field is scrolled into view, focused and briefly
 * highlighted, and a toast says what is missing — instead of the save silently
 * doing nothing or a button sitting greyed out with no explanation.
 */

export const REQUIRED_FIELD_ATTR = 'data-required-field';
const HIGHLIGHT_CLASS = 'field-required-error';
const HIGHLIGHT_MS = 2400;

const timers = new Map<Element, ReturnType<typeof setTimeout>>();

function escapeKey(key: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(key)
    : key.replace(/["\\]/g, '\\$&');
}

export function findRequiredField(key: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(`[${REQUIRED_FIELD_ATTR}="${escapeKey(key)}"]`);
}

/**
 * Scrolls to, focuses and flashes the field. Returns false when the field is
 * not in the DOM (e.g. it lives in a tab that is not mounted).
 */
export function highlightRequiredField(key: string): boolean {
  const el = findRequiredField(key);
  if (!el) return false;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const focusTarget = isFocusable(el)
    ? el
    : el.querySelector<HTMLElement>('input, select, textarea, [contenteditable="true"]');
  // preventScroll: the smooth scroll above already positions the field
  focusTarget?.focus({ preventScroll: true });

  el.classList.add(HIGHLIGHT_CLASS);
  const existing = timers.get(el);
  if (existing) clearTimeout(existing);
  timers.set(el, setTimeout(() => {
    el.classList.remove(HIGHLIGHT_CLASS);
    timers.delete(el);
  }, HIGHLIGHT_MS));

  return true;
}

function isFocusable(el: HTMLElement): boolean {
  return el instanceof HTMLInputElement
    || el instanceof HTMLSelectElement
    || el instanceof HTMLTextAreaElement;
}

export interface RequiredFieldCheck {
  /** Matches the field's data-required-field value. */
  key: string;
  /** True when the field still needs to be filled in. */
  missing: boolean;
  /** Toast text shown for this field. */
  message: string;
  /**
   * Runs before scrolling — use it to open the tab or section holding the
   * field, so it is mounted by the time we try to focus it.
   */
  reveal?: () => void;
}

/**
 * Reports the first missing field and returns true when something was missing,
 * so callers can `if (reportFirstMissingField(...)) return;`.
 */
export function reportFirstMissingField(
  checks: RequiredFieldCheck[],
  showError: (message: string) => void,
): boolean {
  const first = checks.find((c) => c.missing);
  if (!first) return false;

  showError(first.message);
  first.reveal?.();
  // Let a tab switch or section expand render before scrolling to the field
  requestAnimationFrame(() => {
    requestAnimationFrame(() => highlightRequiredField(first.key));
  });
  return true;
}
